import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api'
import { usePlatform } from './PlatformLayout'
import { Badge, Empty, Icon, Kpi, P, PageHeader, ago, fmt, initials, toneFor } from './ui'
import { eventLabel, type AuditEvent } from './Activity'

interface Overview {
  totals: { active: number; suspended: number; patients: number; staff: number; visits_30d: number; visits_prev_30d: number; new_clinics_30d: number }
  visitsByDay: { day: string; visits: number }[]
}

export default function Dashboard() {
  const { clinics } = usePlatform()
  const [ov, setOv] = useState<Overview | null>(null)
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    void api.get<Overview>('/api/platform/overview').then(setOv).catch((e: Error) => setErr(e.message))
    void api.get<{ events: AuditEvent[] }>('/api/platform/audit?limit=8').then((r) => setEvents(r.events)).catch(() => setEvents([]))
  }, [])

  const t = ov?.totals
  const delta = t && t.visits_prev_30d > 0 ? Math.round(((t.visits_30d - t.visits_prev_30d) / t.visits_prev_30d) * 100) : null
  const max = Math.max(1, ...(ov?.visitsByDay.map((d) => d.visits) ?? [1]))
  const busiest = useMemo(() => [...(clinics ?? [])].filter((c) => c.status === 'active').sort((a, b) => b.visitsLast30Days - a.visitsLast30Days).slice(0, 5), [clinics])
  const attention = useMemo(() => {
    const out: { id: string; name: string; why: string; tone: 'warning' | 'danger' | 'neutral' }[] = []
    for (const c of clinics ?? []) {
      if (c.status === 'suspended') out.push({ id: c.id, name: c.name, why: 'Suspended: no one can sign in', tone: 'danger' })
      else if (!c.admins) out.push({ id: c.id, name: c.name, why: 'No active admin', tone: 'warning' })
      else if (c.visitsLast30Days === 0) out.push({ id: c.id, name: c.name, why: 'No visits in 30 days', tone: 'neutral' })
    }
    return out
  }, [clinics])

  return (
    <>
      <PageHeader
        crumbs={['Platform', 'Overview']}
        title="Dashboard"
        description="Every clinic on this server at a glance. Counts only; patient records stay inside each clinic."
        actions={<Link to="/platform/clinics?new=1" className="pa-btn pa-btn-primary"><Icon d={P.plus} />Add clinic</Link>}
      />
      {err && <div className="pa-alert error">{err}</div>}

      <div className="pa-grid-kpi">
        <Kpi label="Active clinics" icon={P.building} tone="primary" value={t ? fmt(t.active) : '—'}
          note={t ? (t.new_clinics_30d ? `${t.new_clinics_30d} new in 30 days` : 'No new clinics in 30 days') : ''} />
        <Kpi label="Patients" icon={P.users} tone="info" value={t ? fmt(t.patients) : '—'} note="Across all clinics" />
        <Kpi label="Visits · 30 days" icon={P.activity} tone="success" value={t ? fmt(t.visits_30d) : '—'} delta={delta} note="vs previous 30 days" />
        <Kpi label="Staff logins" icon={P.key} tone="warning" value={t ? fmt(t.staff) : '—'}
          note={t?.suspended ? `${t.suspended} clinic${t.suspended > 1 ? 's' : ''} suspended` : 'No clinics suspended'} />
      </div>

      <div className="pa-grid-2">
        <div className="pa-card">
          <div className="pa-card-head">
            <div><h3>Visits per day</h3><p>All clinics · last 14 days</p></div>
            <Badge tone="primary">{t ? fmt(ov!.visitsByDay.reduce((n, d) => n + d.visits, 0)) : '—'} visits</Badge>
          </div>
          <div className="pa-card-body">
            {ov ? (
              <div className="pa-bars" role="img" aria-label="Visits per day for the last 14 days">
                {ov.visitsByDay.map((d, i) => (
                  <div key={d.day} className="pa-bar" title={`${d.day}: ${d.visits} visits`}>
                    <span>{d.visits || ''}</span>
                    <i className={i === ov.visitsByDay.length - 1 ? 'today' : ''} style={{ height: `${(d.visits / max) * 140}px` }} />
                    <span>{new Date(`${d.day}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric' })}</span>
                  </div>
                ))}
              </div>
            ) : <div className="pa-skel" style={{ height: 180 }} />}
          </div>
        </div>

        <div className="pa-card">
          <div className="pa-card-head"><div><h3>Needs attention</h3><p>Clinics to look at</p></div><Badge tone={attention.length ? 'warning' : 'success'}>{attention.length}</Badge></div>
          {clinics && attention.length === 0 ? (
            <Empty icon={P.shield} title="All clear">Every clinic is active, has an admin and saw patients this month.</Empty>
          ) : (
            <ul className="pa-feed">
              {attention.slice(0, 6).map((a) => (
                <li key={a.id + a.why}>
                  <span className={`ic t-${a.tone}`}><Icon d={a.tone === 'danger' ? P.pause : P.building} size={14} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}><b>{a.name}</b><small>{a.why}</small></div>
                  <Link to={`/platform/clinics?open=${a.id}`} className="pa-btn pa-btn-sm">Open</Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="pa-grid-2">
        <div className="pa-card">
          <div className="pa-card-head"><div><h3>Busiest clinics</h3><p>By visits in the last 30 days</p></div><Link to="/platform/clinics" className="pa-btn pa-btn-sm">View all</Link></div>
          <div className="pa-table-wrap">
            <table className="pa-table" style={{ minWidth: 520 }}>
              <thead><tr><th>Clinic</th><th className="num">Patients</th><th className="num">Visits</th><th>Last visit</th></tr></thead>
              <tbody>
                {busiest.map((c) => (
                  <tr key={c.id}>
                    <td><div className="pa-cell"><span className={`pa-avatar t-${toneFor(c.name)}`}>{initials(c.name)}</span><div><b>{c.name}</b><small>{c.slug}</small></div></div></td>
                    <td className="num">{fmt(c.patients)}</td>
                    <td className="num">{fmt(c.visitsLast30Days)}</td>
                    <td className="muted">{ago(c.lastVisit ? `${c.lastVisit}T12:00:00` : null)}</td>
                  </tr>
                ))}
                {clinics && busiest.length === 0 && <tr><td colSpan={4}><Empty icon={P.building} title="No active clinics yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="pa-card">
          <div className="pa-card-head"><div><h3>Recent activity</h3><p>Across all clinics</p></div><Link to="/platform/activity" className="pa-btn pa-btn-sm">View all</Link></div>
          {events && events.length === 0 && <Empty icon={P.list} title="Nothing yet" />}
          <ul className="pa-feed">
            {(events ?? []).map((e) => {
              const l = eventLabel(e)
              return (
                <li key={e.id}>
                  <span className={`ic t-${l.tone}`}><Icon d={l.icon} size={14} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}><b>{e.user_name ?? 'System'}</b> {l.text}<small>{e.clinic ?? 'Platform'} · {ago(e.at)}</small></div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </>
  )
}
